'use client';
import { Box, SxProps, useTheme } from '@mui/material';
import { IntersectionOptions, useInView } from 'react-intersection-observer';
import type {
  MuxPlayerRefAttributes,
  MuxPlayerProps,
} from '@mux/mux-player-react';
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  Suspense,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import dynamic from 'next/dynamic';

const MuxPlayer = dynamic(
  async () => {
    await import('@mux/mux-player-react/themes/microvideo');
    return import('@mux/mux-player-react');
  },
  { ssr: false },
);

export type VideoRefAttributes = MuxPlayerRefAttributes;

type Props = MuxPlayerProps & {
  playbackId: string;
  aspectRatio: number | string;
  blurDataURL?: string;
  withAutoPlay?: boolean;
  poster?: string;
  theme?: 'micro' | 'default';
  inViewOptions?: IntersectionOptions;
  sx?: SxProps;
  controls?: boolean;
};

const Video: ForwardRefExoticComponent<
  Props & RefAttributes<MuxPlayerRefAttributes>
> = forwardRef<MuxPlayerRefAttributes, Props>(
  (
    {
      aspectRatio,
      blurDataURL,
      withAutoPlay,
      sx,
      inViewOptions,
      controls,
      poster,
      theme,
      ...rest
    },
    forwardedRef,
  ) => {
    const internalAspectRatio =
      typeof aspectRatio === 'string'
        ? aspectRatio.replace(':', ' / ')
        : aspectRatio;

    const muiTheme = useTheme();
    const internalRef = useRef<MuxPlayerRefAttributes>(null);
    const { ref: observerRef, inView } = useInView(inViewOptions);

    const setRefs = (element: MuxPlayerRefAttributes | null) => {
      internalRef.current = element;
      observerRef(element);

      if (typeof forwardedRef === 'function') {
        forwardedRef(element);
      } else if (forwardedRef) {
        forwardedRef.current = element;
      }
    };

    useEffect(() => {
      if (!withAutoPlay) return;
      const player = internalRef.current;
      if (!player) return;

      if (inView) {
        player.play().catch((error) => {
          console.warn('Browser blocked autoplay:', error);
        });
      } else {
        player.pause();
      }
    }, [inView, withAutoPlay]);

    const videoTheme = useMemo(() => {
      if (theme === 'micro' || !theme) return 'microvideo';
      return undefined;
    }, []);

    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          borderRadius: 0.6,
          overflow: 'hidden',
          aspectRatio: internalAspectRatio,
          ...sx,
        }}
      >
        <Suspense>
          <MuxPlayer
            ref={setRefs}
            muted={withAutoPlay}
            theme={videoTheme}
            poster={poster}
            placeholder={blurDataURL}
            accentColor={muiTheme.palette.primary.main}
            style={{
              objectFit: 'contain',
              ...(!controls && {
                '--live-button': 'none',
                '--seek-backward-button': 'none',
                '--seek-forward-button': 'none',
                '--captions-button': 'none',
                '--rendition-menu-button': 'none',
                '--airplay-button': 'none',
                '--pip-button': 'none',
                '--cast-button': 'none',
                '--playback-rate-button': 'none',
                '--time-range': 'none',
                '--time-display': 'none',
                '--duration-display': 'none',
              }),
            }}
            {...rest}
          />
        </Suspense>
      </Box>
    );
  },
);
Video.displayName = 'TBVideo';

export default Video;
